import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAppContext, useAppActions } from '../../context/AppContext';
import ReportingEngine from '../../services/reporting/ReportingEngine';
import { REPORT_FREQUENCIES, REPORT_CATEGORIES, FIELD_CATALOG, EXPORT_FORMATS } from '../../data/reportTemplates';
import {
  FileText, Plus, Search, Calendar, Download,
  Printer, Eye, TrendingUp,
  BarChart3, Activity, Users, DollarSign,
  AlertTriangle, CheckCircle, Clock, Target, Star,
  ChevronDown, ChevronUp,
  Brain, Cpu, Sparkles, Zap, Globe, Shield,
  LayoutDashboard, Settings, Mail, Play, Pause,
  Trash2, Save, GripVertical, X, RefreshCw,
  Building2, Pill, ClipboardList, ArrowLeftRight,
  ListOrdered, Wrench, UtensilsCrossed, Heart,
  Award, Lock, MessageSquare, BookOpen, FileCheck,
  Bug, TestTube, Stethoscope, Siren, Table, HelpCircle
} from 'lucide-react';
import { Tooltip, EmptyState } from '../Common/ReusableUI';
import { TableSkeleton, CardSkeleton } from '../Common/SkeletonLoaders';

const ICON_MAP = {
  ClipboardList, Pill, Clock, Building2, AlertTriangle, ArrowLeftRight,
  Stethoscope, ListOrdered, Users, Wrench, UtensilsCrossed,
  Heart, Award, DollarSign, Shield, Bug, Brain, Lock, MessageSquare,
  BarChart3, TrendingUp, Star, TestTube, BookOpen, FileCheck, Siren,
  FileText, Globe
};

const getIcon = (iconName) => ICON_MAP[iconName] || FileText;

const FIELD_TOOLTIPS = {
  resident_name: 'Full name of the resident as recorded at admission',
  resident_id: 'Unique system identifier for each resident record',
  room_number: 'Current room allocation within the facility',
  wing: 'Wing or ward the resident is assigned to',
  admission_date: 'Date the resident was officially admitted',
  anacc_class: 'AN-ACC funding classification from the latest assessment',
  care_minutes_target: 'Required care minutes per day based on AN-ACC class',
  care_minutes_actual: 'Delivered care minutes recorded for the period',
  rn_minutes_target: 'Required registered-nurse minutes per day',
  rn_minutes_actual: 'Delivered RN minutes recorded for the period',
  medication_name: 'Name of the prescribed medication',
  medication_dose: 'Prescribed dosage amount and unit',
  administration_time: 'Scheduled or actual administration timestamp',
  administered_by: 'Staff member who administered the medication',
  incident_type: 'Classification of the incident (fall, medication error, etc.)',
  incident_severity: 'Severity rating: Low, Medium, High, or Critical',
  incident_date: 'Date and time the incident occurred',
  incident_status: 'Current investigation/resolution status',
  assessment_type: 'Type of clinical assessment performed',
  assessment_score: 'Numerical score from the assessment tool',
  assessment_date: 'Date the assessment was completed',
  care_plan_status: 'Current status of the resident care plan',
  care_plan_goals: 'Number of active goals in the care plan',
  staff_name: 'Full name of the staff member',
  staff_role: 'Role or position of the staff member',
  shift_date: 'Date the shift was scheduled or worked',
  shift_hours: 'Total hours worked in the shift',
  occupancy_count: 'Current number of occupied beds',
  occupancy_rate: 'Percentage of available beds currently occupied',
  revenue: 'Total revenue for the reporting period',
  expenses: 'Total expenses for the reporting period',
  enquiry_source: 'Where the admission enquiry originated from',
  enquiry_status: 'Current stage in the enquiry pipeline',
  waitlist_position: 'Position on the facility waitlist',
  nqip_indicator: 'National Quality Indicator Programme metric name',
  nqip_value: 'Current value for the NQIP indicator',
  star_rating: 'Overall star rating (1-5) from quality assessments',
  complaint_type: 'Category of the complaint or feedback',
  complaint_status: 'Resolution status of the complaint',
  infection_type: 'Type of infection recorded',
  antimicrobial_name: 'Name of the prescribed antimicrobial agent',
  restrictive_practice_type: 'Type of restrictive practice applied',
  psychotropic_medication: 'Psychotropic medication prescribed to the resident',
};

const GOV_REPORT_TOOLTIPS = {
  'Department of Health and Aged Care': 'DoHAC — primary federal body overseeing aged care funding, regulation, and policy',
  'Aged Care Quality and Safety Commission': 'ACQSC — independent body ensuring quality and safety standards in aged care',
};

const FREQ_COLORS = {
  daily: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', gradient: 'from-blue-500 to-blue-600' },
  weekly: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', gradient: 'from-green-500 to-green-600' },
  monthly: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', gradient: 'from-purple-500 to-purple-600' },
  quarterly: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', gradient: 'from-amber-500 to-amber-600' },
  annual: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', gradient: 'from-red-500 to-red-600' },
  ad_hoc: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', gradient: 'from-gray-500 to-gray-600' }
};

const getCellStyle = (key, value) => {
  const s = String(value);
  if (['Compliant', 'Covered', 'Administered', 'Resolved', 'Improving', 'Favourable', 'Current', 'Satisfied', 'Active', 'Healing', 'Yes'].includes(s))
    return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (['Below Target', 'Stable', 'Under Investigation', 'Open', 'Medium', 'Expired', 'N/A', 'New'].includes(s))
    return 'bg-amber-50 text-amber-700 border border-amber-200';
  if (['Unfavourable', 'Critical', 'High', 'Missed', 'Unsatisfied', 'Deteriorated', 'Gap (agency filled)', 'No'].includes(s))
    return 'bg-red-50 text-red-700 border border-red-200';
  if (typeof value === 'number' && (key.includes('amount') || key.includes('budget') || key.includes('cost')))
    return value < 0 ? 'text-red-600 font-semibold tabular-nums' : 'text-emerald-600 font-semibold tabular-nums';
  if (s.endsWith('%')) {
    const n = parseFloat(s);
    return n >= 90 ? 'text-emerald-600 font-bold tabular-nums' : n >= 70 ? 'text-amber-600 font-bold tabular-nums' : 'text-red-600 font-bold tabular-nums';
  }
  return '';
};

const formatCellDisplay = (key, value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number' && (key.includes('amount') || key.includes('budget') || key.includes('revenue') || key.includes('cost') || key.includes('expenses')))
    return `${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString()}`;
  return String(value);
};

const REPORT_TYPE_MAP = {
  'quarterly-nqip-review': 'quality', 'gov-nqip-quarterly': 'quality',
  'monthly-facility-quality': 'quality', 'quarterly-star-rating': 'quality',
  'monthly-financial-performance': 'financial', 'gov-qfr': 'financial', 'gov-acfr': 'financial',
  'daily-care-minutes': 'compliance', 'gov-care-minutes-monthly': 'compliance', 'gov-rn-coverage': 'compliance',
  'daily-incident-summary': 'incidents', 'monthly-infection-control': 'incidents',
};

const GOV_BODY_STYLES = {
  'Department of Health and Aged Care': { icon: Shield, color: 'blue', abbr: 'DoHAC' },
  'Aged Care Quality and Safety Commission': { icon: Award, color: 'purple', abbr: 'ACQSC' },
};

const QualityIndicatorGrid = ({ data }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {data.map((row, i) => {
      const label = row.indicator || row.sub_category || `Indicator ${i + 1}`;
      const value = row.value || row.score || '—';
      const benchmark = row.national_avg || row.benchmark || row.weight || '—';
      const trend = row.trend || '';
      const numVal = parseFloat(String(value));
      const benchNum = parseFloat(String(benchmark));
      const isMet = !isNaN(numVal) && !isNaN(benchNum) && numVal >= benchNum;
      const pctWidth = Math.min(Math.abs(numVal) || 50, 100);
      return (
        <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all group">
          <div className="flex items-start justify-between mb-3">
            <h5 className="font-semibold text-slate-800 text-sm leading-tight flex-1 pr-2">{label}</h5>
            {trend && (
              <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
                trend === 'Improving' ? 'bg-emerald-100 text-emerald-700' : trend === 'Stable' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
              }`}>
                <TrendingUp className={`w-3 h-3 ${trend === 'Improving' ? '' : trend === 'Stable' ? 'rotate-0' : 'rotate-180'}`} />
                {trend}
              </span>
            )}
          </div>
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className={`text-3xl font-extrabold tracking-tight ${isMet ? 'text-emerald-600' : 'text-amber-600'}`}>{value}</div>
              <div className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                <Target className="w-3 h-3" /> Benchmark: {benchmark}
              </div>
            </div>
            <div className="w-28 space-y-1">
              <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <div className={`h-2.5 rounded-full transition-all duration-700 ${isMet ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pctWidth}%` }} />
              </div>
              <div className={`text-[10px] text-right font-medium ${isMet ? 'text-emerald-600' : 'text-amber-600'}`}>{isMet ? 'Above' : 'Below'} benchmark</div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const FinancialStatement = ({ data, dateRange }) => {
  const netPosition = data.reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-5">
        <h4 className="text-white font-bold text-lg flex items-center gap-2"><DollarSign className="w-5 h-5" /> Financial Statement</h4>
        <p className="text-slate-300 text-sm mt-1">Period: {dateRange?.start || 'Current'} — {dateRange?.end || 'Present'}</p>
      </div>
      <div className="divide-y divide-slate-100">
        {data.map((row, i) => {
          const amount = row.amount || 0;
          const budget = row.budget || 0;
          const variance = row.variance || '';
          const isRevenue = amount > 0;
          return (
            <div key={i} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-3 flex-1">
                <div className={`w-1.5 h-10 rounded-full ${isRevenue ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <span className="text-slate-800 font-medium">{row.category || `Line ${i + 1}`}</span>
              </div>
              <div className="flex items-center gap-10 text-right">
                <div className="w-28">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Actual</div>
                  <div className={`font-bold text-base tabular-nums ${isRevenue ? 'text-emerald-600' : 'text-red-600'}`}>
                    {isRevenue ? '' : '-'}${Math.abs(amount).toLocaleString()}
                  </div>
                </div>
                <div className="w-28">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Budget</div>
                  <div className="text-slate-600 tabular-nums">
                    {budget >= 0 ? '' : '-'}${Math.abs(budget).toLocaleString()}
                  </div>
                </div>
                <div className="w-24">
                  {variance && (
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                      variance === 'Favourable' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>{variance === 'Favourable' ? '✓' : '✗'} {variance}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-slate-400" />
          <span className="font-bold text-lg">Net Position</span>
        </div>
        <span className={`text-2xl font-extrabold tracking-tight ${netPosition >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {netPosition >= 0 ? '+' : '-'}${Math.abs(netPosition).toLocaleString()}
        </span>
      </div>
    </div>
  );
};

const ComplianceDashboard = ({ data }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {data.map((row, i) => {
      const target = row.care_minutes_target || 0;
      const actual = row.care_minutes_actual || 0;
      const rnTarget = row.rn_minutes_target || 0;
      const rnActual = row.rn_minutes_actual || 0;
      const compliance = row.compliance || row.declaration || row.shift_am || 'N/A';
      const pct = target > 0 ? Math.min((actual / target) * 100, 110) : 100;
      const rnPct = rnTarget > 0 ? Math.min((rnActual / rnTarget) * 100, 110) : 100;
      const isCompliant = ['Compliant', 'Covered'].includes(compliance);
      return (
        <div key={i} className={`bg-white rounded-xl border-2 p-5 shadow-sm transition-all ${isCompliant ? 'border-emerald-200 hover:border-emerald-300' : 'border-red-200 hover:border-red-300'}`}>
          <div className="flex items-center justify-between mb-4">
            <h5 className="font-bold text-slate-800 text-base">{row.wing || row.date || `Item ${i + 1}`}</h5>
            <span className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${isCompliant ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {isCompliant ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              {isCompliant ? 'Compliant' : 'Below Target'}
            </span>
          </div>
          {target > 0 && (
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-slate-600 font-medium">Total Care Minutes</span>
                  <span className={`font-bold tabular-nums ${pct >= 100 ? 'text-emerald-600' : 'text-red-600'}`}>{actual} / {target} min</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3.5 overflow-hidden">
                  <div className={`h-3.5 rounded-full transition-all duration-700 ${pct >= 100 ? 'bg-emerald-500' : pct >= 90 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
                <div className="text-xs text-slate-400 mt-1 text-right">{pct.toFixed(1)}% of target</div>
              </div>
              {rnTarget > 0 && (
                <div>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-slate-600 font-medium">RN Minutes</span>
                    <span className={`font-bold tabular-nums ${rnPct >= 100 ? 'text-emerald-600' : 'text-red-600'}`}>{rnActual} / {rnTarget} min</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3.5 overflow-hidden">
                    <div className={`h-3.5 rounded-full transition-all duration-700 ${rnPct >= 100 ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${Math.min(rnPct, 100)}%` }} />
                  </div>
                  <div className="text-xs text-slate-400 mt-1 text-right">{rnPct.toFixed(1)}% of target</div>
                </div>
              )}
            </div>
          )}
          {row.shift_am && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {['AM', 'PM', 'Night'].map((shift, idx) => {
                const val = row[`shift_${shift.toLowerCase()}`] || row[`shift_${['am','pm','night'][idx]}`];
                const ok = val === 'Covered';
                return (
                  <div key={shift} className={`text-center p-2 rounded-lg text-xs font-bold ${ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {shift}: {ok ? '✓' : '!'}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const EnhancedTable = ({ data }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
            <th className="px-4 py-3.5 text-left font-bold text-slate-500 text-[10px] uppercase tracking-widest w-10">#</th>
            {Object.keys(data[0]).map(col => (
              <th key={col} className="px-4 py-3.5 text-left font-bold text-slate-500 text-[10px] uppercase tracking-widest whitespace-nowrap">
                {col.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {data.map((row, i) => (
            <tr key={i} className="hover:bg-blue-50/40 transition-colors group">
              <td className="px-4 py-3 text-xs text-slate-400 font-mono">{i + 1}</td>
              {Object.entries(row).map(([key, val], j) => {
                const style = getCellStyle(key, val);
                const display = formatCellDisplay(key, val);
                return (
                  <td key={j} className="px-4 py-3 whitespace-nowrap">
                    {style ? (
                      <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-semibold ${style}`}>{display}</span>
                    ) : (
                      <span className="text-slate-700">{display}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Reports = () => {
  const { state } = useAppContext();
  const actions = useAppActions();
  const { currentUser, selectedFacility } = state;

  const [activeTab, setActiveTab] = useState('prebuilt');
  const [searchQuery, setSearchQuery] = useState('');
  const [frequencyFilter, setFrequencyFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [generatedReports, setGeneratedReports] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState({ templateId: '', name: '', frequency: 'weekly', recipients: '', exportFormat: 'pdf' });
  const [customFields, setCustomFields] = useState([]);
  const [customReportName, setCustomReportName] = useState('');
  const [customExportFormat, setCustomExportFormat] = useState('pdf');
  const [dashboardTiles, setDashboardTiles] = useState([]);
  const [expandedGovReport, setExpandedGovReport] = useState(null);
  const [govPreviewData, setGovPreviewData] = useState({});
  const [govPreviewLoading, setGovPreviewLoading] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const dateRangeError = useMemo(() => {
    if (dateStart && dateEnd && dateEnd < dateStart) return 'End date must be after start date';
    return '';
  }, [dateStart, dateEnd]);
  const viewerRef = useRef(null);

  const handleGovPreview = useCallback(async (reportId) => {
    if (govPreviewData[reportId]) return;
    setGovPreviewLoading(reportId);
    const result = await ReportingEngine.generateReport(reportId, {
      facilityId: selectedFacility || 'all',
      userId: currentUser?.id
    });
    if (result.success) {
      setGovPreviewData(prev => ({ ...prev, [reportId]: result.data }));
      setGeneratedReports(ReportingEngine.getGeneratedReports());
    }
    setGovPreviewLoading(null);
  }, [govPreviewData, selectedFacility, currentUser]);

  const handleExpandGov = useCallback((reportId) => {
    const newId = expandedGovReport === reportId ? null : reportId;
    setExpandedGovReport(newId);
    if (newId && !govPreviewData[newId]) {
      handleGovPreview(newId);
    }
  }, [expandedGovReport, govPreviewData, handleGovPreview]);

  useEffect(() => {
    setIsLoading(true);
    setGeneratedReports(ReportingEngine.getGeneratedReports());
    setSchedules(ReportingEngine.getSchedules());
    const dashResult = ReportingEngine.getDashboardConfig(currentUser?.id, currentUser?.role);
    if (dashResult.success) setDashboardTiles(dashResult.data.tiles || []);
    setIsLoading(false);
  }, [currentUser]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (viewingReport) setViewingReport(null);
        else if (showScheduleModal) setShowScheduleModal(false);
      }
      if (e.ctrlKey && e.key === 'p' && viewingReport) {
        e.preventDefault();
        window.print();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [viewingReport, showScheduleModal]);

  const prebuiltReports = useMemo(() => ReportingEngine.getPrebuiltReports(), []);
  const governmentReports = useMemo(() => ReportingEngine.getGovernmentReportTemplates(), []);
  const fieldCatalog = useMemo(() => FIELD_CATALOG, []);

  const filteredPrebuilt = useMemo(() => {
    return prebuiltReports.filter(r => {
      const matchesSearch = r.name.toLowerCase().includes(searchQuery.toLowerCase())
        || r.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFreq = frequencyFilter === 'all' || r.frequency === frequencyFilter;
      const matchesCat = categoryFilter === 'all' || r.category === categoryFilter;
      return matchesSearch && matchesFreq && matchesCat;
    });
  }, [prebuiltReports, searchQuery, frequencyFilter, categoryFilter]);

  const reportsByFrequency = useMemo(() => {
    const grouped = {};
    filteredPrebuilt.forEach(r => {
      if (!grouped[r.frequency]) grouped[r.frequency] = [];
      grouped[r.frequency].push(r);
    });
    return grouped;
  }, [filteredPrebuilt]);

  const stats = useMemo(() => ({
    total: prebuiltReports.length,
    generated: generatedReports.length,
    scheduled: schedules.filter(s => s.isActive).length,
    government: governmentReports.length
  }), [prebuiltReports, generatedReports, schedules, governmentReports]);

  const handleGenerate = useCallback(async (templateId) => {
    setGenerating(templateId);
    const result = await ReportingEngine.generateReport(templateId, {
      facilityId: selectedFacility || 'all',
      userId: currentUser?.id
    });
    setGenerating(null);
    if (result.success) {
      setGeneratedReports(ReportingEngine.getGeneratedReports());
      actions.showToast?.(`Report generated: ${result.data.templateName}`, 'success');
      setViewingReport(result.data);
    } else {
      actions.showToast?.(`Generation failed: ${result.error}`, 'error');
    }
  }, [selectedFacility, currentUser, actions]);

  const handleExport = useCallback((reportId, format) => {
    const result = ReportingEngine.exportReport(reportId, format);
    if (result.success) {
      actions.showToast?.(`Exported as ${format.toUpperCase()}`, 'success');
    }
  }, [actions]);

  const handleCreateSchedule = useCallback(() => {
    const result = ReportingEngine.createSchedule({
      ...scheduleConfig,
      recipients: scheduleConfig.recipients.split(',').map(r => r.trim()).filter(Boolean),
      userId: currentUser?.id
    });
    if (result.success) {
      setSchedules(ReportingEngine.getSchedules());
      setShowScheduleModal(false);
      setScheduleConfig({ templateId: '', name: '', frequency: 'weekly', recipients: '', exportFormat: 'pdf' });
      actions.showToast?.('Schedule created', 'success');
    }
  }, [scheduleConfig, currentUser, actions]);

  const handleDeleteSchedule = useCallback((id) => {
    ReportingEngine.deleteSchedule(id);
    setSchedules(ReportingEngine.getSchedules());
    actions.showToast?.('Schedule deleted', 'success');
  }, [actions]);

  const handleToggleSchedule = useCallback((id, isActive) => {
    ReportingEngine.updateSchedule(id, { isActive: !isActive });
    setSchedules(ReportingEngine.getSchedules());
  }, []);

  const handleRunSchedule = useCallback(async (id) => {
    setGenerating(id);
    await ReportingEngine.runScheduledReport(id);
    setGenerating(null);
    setSchedules(ReportingEngine.getSchedules());
    setGeneratedReports(ReportingEngine.getGeneratedReports());
    actions.showToast?.('Scheduled report executed', 'success');
  }, [actions]);

  const handleGenerateCustom = useCallback(async () => {
    if (customFields.length === 0) {
      actions.showToast?.('Select at least one field', 'error');
      return;
    }
    setGenerating('custom');
    const result = await ReportingEngine.generateCustomReport({
      name: customReportName || 'Custom Report',
      selectedFields: customFields,
      facilityId: selectedFacility || 'all',
      exportFormat: customExportFormat,
      userId: currentUser?.id
    });
    setGenerating(null);
    if (result.success) {
      setGeneratedReports(ReportingEngine.getGeneratedReports());
      setViewingReport(result.data);
      actions.showToast?.('Custom report generated', 'success');
    }
  }, [customFields, customReportName, customExportFormat, selectedFacility, currentUser, actions]);

  const handleSaveDashboard = useCallback(() => {
    ReportingEngine.saveDashboardConfig(currentUser?.id, dashboardTiles);
    actions.showToast?.('Dashboard layout saved', 'success');
  }, [currentUser, dashboardTiles, actions]);

  const toggleCustomField = useCallback((fieldId) => {
    setCustomFields(prev => prev.includes(fieldId) ? prev.filter(f => f !== fieldId) : [...prev, fieldId]);
  }, []);

  // ─── TAB DEFINITIONS ────────────────────────────────
  const tabs = [
    { id: 'prebuilt', label: 'Pre-built Reports', icon: FileText },
    { id: 'custom', label: 'Custom Builder', icon: Settings },
    { id: 'scheduled', label: 'Scheduled', icon: Clock },
    { id: 'dashboard', label: 'Dashboard Builder', icon: LayoutDashboard },
    { id: 'government', label: 'Government Reports', icon: Globe },
    { id: 'history', label: 'Report History', icon: Activity },
    { id: 'ai', label: 'AI Insights', icon: Brain }
  ];

  // ─── RENDER ──────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              Reporting Engine
              <FileText className="w-8 h-8 text-indigo-300 animate-pulse" />
            </h1>
            <p className="text-base mt-2 opacity-90">
              Pre-built reports, custom builder, scheduled delivery &amp; government submissions
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowScheduleModal(true)}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl font-semibold transition-all flex items-center gap-2"
            >
              <Clock className="w-4 h-4" />
              New Schedule
            </button>
            <button
              onClick={() => setActiveTab('custom')}
              className="bg-white text-indigo-600 px-4 py-2 rounded-xl font-semibold hover:bg-opacity-90 transition-all flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Custom Report
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Pre-built Templates', value: stats.total, icon: FileText, color: 'blue', trend: '28 templates' },
          { label: 'Reports Generated', value: stats.generated, icon: CheckCircle, color: 'green', trend: 'All time' },
          { label: 'Active Schedules', value: stats.scheduled, icon: Clock, color: 'purple', trend: 'Auto-running' },
          { label: 'Government Reports', value: stats.government, icon: Globe, color: 'red', trend: 'Regulatory' }
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white rounded-2xl p-5 shadow-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 bg-${stat.color}-100 rounded-lg`}>
                  <Icon className={`w-5 h-5 text-${stat.color}-600`} />
                </div>
                <div>
                  <h3 className="font-bold text-xl">{stat.value}</h3>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <span className={`text-xs text-${stat.color}-600`}>{stat.trend}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex space-x-1 px-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Skeleton while initial data loads */}
          {isLoading && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </div>
              <TableSkeleton rows={4} columns={4} />
            </div>
          )}

          {/* Search & Filters (for prebuilt and history tabs) */}
          {(activeTab === 'prebuilt' || activeTab === 'history') && (
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <select
                value={frequencyFilter}
                onChange={(e) => setFrequencyFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Frequencies</option>
                {Object.entries(REPORT_FREQUENCIES).map(([key, val]) => (
                  <option key={key} value={val}>{key.charAt(0) + key.slice(1).toLowerCase()}</option>
                ))}
              </select>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Categories</option>
                {Object.entries(REPORT_CATEGORIES).map(([key, val]) => (
                  <option key={key} value={val}>{key.charAt(0) + key.slice(1).toLowerCase()}</option>
                ))}
              </select>
            </div>
          )}

          {/* ─── TAB: PRE-BUILT REPORTS ──────────────── */}
          {activeTab === 'prebuilt' && !isLoading && (
            <div className="space-y-10">
              {Object.entries(reportsByFrequency).map(([freq, reports]) => {
                const colors = FREQ_COLORS[freq] || FREQ_COLORS.ad_hoc;
                return (
                  <div key={freq}>
                    <div className="flex items-center gap-3 mb-5">
                      <div className={`w-10 h-10 bg-gradient-to-br ${colors.gradient} rounded-xl flex items-center justify-center shadow-sm`}>
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-800 capitalize">{freq} Reports</h3>
                        <p className="text-xs text-slate-500">{reports.length} templates available</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {reports.map((report) => {
                        const Icon = getIcon(report.icon);
                        const isGenerating = generating === report.id;
                        return (
                          <div
                            key={report.id}
                            className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-xl hover:border-slate-300 transition-all duration-300 group relative overflow-hidden"
                          >
                            <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${colors.gradient} opacity-60 group-hover:opacity-100 transition-opacity`} />
                            <div className="flex items-start justify-between mb-4">
                              <div className={`p-3 bg-gradient-to-br ${colors.gradient} rounded-xl shadow-sm group-hover:scale-110 group-hover:shadow-md transition-all duration-300`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg ${colors.bg} ${colors.text} border ${colors.border}`}>
                                {report.category}
                              </span>
                            </div>
                            <h4 className="font-bold text-slate-900 mb-1.5 leading-tight">{report.name}</h4>
                            <p className="text-xs text-slate-500 mb-4 line-clamp-2 leading-relaxed">{report.description}</p>
                            <div className="flex items-center gap-1.5 mb-4">
                              {report.exportFormats.map(fmt => (
                                <span key={fmt} className="px-2 py-0.5 bg-slate-50 text-slate-500 text-[10px] rounded-md uppercase font-bold tracking-wider border border-slate-200">{fmt}</span>
                              ))}
                              {report.dataSources && (
                                <span className="px-2 py-0.5 bg-indigo-50 text-indigo-500 text-[10px] rounded-md font-bold ml-auto">
                                  {report.dataSources.length} sources
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                              <button
                                onClick={() => handleGenerate(report.id)}
                                disabled={isGenerating}
                                className={`flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                                  isGenerating
                                    ? 'bg-slate-100 text-slate-400 cursor-wait'
                                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg hover:shadow-indigo-200'
                                }`}
                              >
                                {isGenerating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                                {isGenerating ? 'Generating…' : 'Generate Report'}
                              </button>
                              <button
                                onClick={() => {
                                  setScheduleConfig(prev => ({ ...prev, templateId: report.id, name: report.name }));
                                  setShowScheduleModal(true);
                                }}
                                className="p-2.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors border border-slate-200 hover:border-purple-200"
                                title="Schedule"
                              >
                                <Clock className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {filteredPrebuilt.length === 0 && (
                <EmptyState
                  icon={Search}
                  title="No reports match your filters"
                  description="Try adjusting your search query, frequency, or category filter to see results."
                  action={
                    <button
                      onClick={() => { setSearchQuery(''); setFrequencyFilter('all'); setCategoryFilter('all'); }}
                      className="mt-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-semibold hover:bg-indigo-200"
                    >
                      Clear All Filters
                    </button>
                  }
                />
              )}
            </div>
          )}

          {/* ─── TAB: CUSTOM BUILDER ─────────────────── */}
          {activeTab === 'custom' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                <h3 className="text-lg font-bold text-indigo-800 mb-2 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Custom Report Builder
                </h3>
                <p className="text-sm text-indigo-600">Select fields from any module, apply filters, and export in your preferred format.</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Field Selection */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-800">Available Fields</h4>
                    <span className="text-sm text-indigo-600">{customFields.length} selected</span>
                  </div>
                  {(() => {
                    const modules = [...new Set(fieldCatalog.map(f => f.module))];
                    return modules.map(mod => (
                      <div key={mod} className="border border-gray-200 rounded-lg">
                        <div className="px-4 py-3 bg-gray-50 rounded-t-lg">
                          <h5 className="font-medium text-sm text-gray-700 capitalize">{mod.replace('_', ' ')}</h5>
                        </div>
                        <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                          {fieldCatalog.filter(f => f.module === mod).map(field => (
                            <Tooltip key={field.id} content={FIELD_TOOLTIPS[field.id] || `${field.label} (${field.type})`} position="top">
                              <button
                                onClick={() => toggleCustomField(field.id)}
                                className={`w-full px-3 py-2 rounded-lg text-xs font-medium transition-all text-left flex items-center gap-2 ${
                                  customFields.includes(field.id)
                                    ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                                    : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:border-gray-200'
                                }`}
                              >
                                {customFields.includes(field.id) && <CheckCircle className="w-3 h-3" />}
                                {field.label}
                              </button>
                            </Tooltip>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* Config Panel */}
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-5 sticky top-4">
                    <h4 className="font-semibold text-gray-800 mb-4">Report Configuration</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Report Name</label>
                        <input
                          type="text"
                          value={customReportName}
                          onChange={(e) => setCustomReportName(e.target.value)}
                          placeholder="My Custom Report"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Export Format</label>
                        <select
                          value={customExportFormat}
                          onChange={(e) => setCustomExportFormat(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                        >
                          {EXPORT_FORMATS.map(f => (
                            <option key={f} value={f}>{f.toUpperCase()}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            value={dateStart}
                            onChange={(e) => setDateStart(e.target.value)}
                            className={`flex-1 px-2 py-1.5 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 ${dateRangeError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                          />
                          <span className="text-xs text-gray-400">to</span>
                          <input
                            type="date"
                            value={dateEnd}
                            onChange={(e) => setDateEnd(e.target.value)}
                            className={`flex-1 px-2 py-1.5 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 ${dateRangeError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                          />
                        </div>
                        {dateRangeError && (
                          <p className="mt-1 text-[11px] font-medium text-red-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> {dateRangeError}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Selected Fields</label>
                        {customFields.length === 0 ? (
                          <EmptyState
                            icon={ClipboardList}
                            title="No fields selected"
                            description="Click fields on the left to add them to your custom report."
                          />
                        ) : (
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {customFields.map(fId => {
                              const field = fieldCatalog.find(f => f.id === fId);
                              return (
                                <div key={fId} className="flex items-center justify-between bg-indigo-50 px-3 py-1.5 rounded-lg">
                                  <span className="text-xs text-indigo-700">{field?.label}</span>
                                  <button onClick={() => toggleCustomField(fId)} className="text-indigo-400 hover:text-red-500">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleGenerateCustom}
                        disabled={customFields.length === 0 || generating === 'custom' || !!dateRangeError}
                        className={`w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                          customFields.length === 0 || dateRangeError
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : generating === 'custom'
                            ? 'bg-indigo-200 text-indigo-500 cursor-wait'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700'
                        }`}
                      >
                        {generating === 'custom' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                        {generating === 'custom' ? 'Generating…' : 'Generate Custom Report'}
                      </button>
                      {generating === 'custom' && (
                        <div className="mt-3">
                          <TableSkeleton rows={3} columns={3} />
                        </div>
                      )}
                      <button
                        onClick={() => {
                          if (customFields.length > 0) {
                            ReportingEngine.saveCustomReportTemplate({
                              name: customReportName || 'Custom Template',
                              selectedFields: customFields,
                              exportFormat: customExportFormat,
                              userId: currentUser?.id
                            });
                            actions.showToast?.('Template saved', 'success');
                          }
                        }}
                        disabled={customFields.length === 0}
                        className="w-full py-2 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 border border-indigo-300 text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Save className="w-4 h-4" />
                        Save as Template
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── TAB: SCHEDULED REPORTS ──────────────── */}
          {activeTab === 'scheduled' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800">Scheduled Reports</h3>
                <button
                  onClick={() => setShowScheduleModal(true)}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4" />
                  New Schedule
                </button>
              </div>

              {schedules.length === 0 ? (
                <EmptyState
                  icon={Clock}
                  title="No scheduled reports yet"
                  description="Create a schedule to auto-generate and email reports on a recurring basis."
                  action={
                    <button
                      onClick={() => setShowScheduleModal(true)}
                      className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      New Schedule
                    </button>
                  }
                />
              ) : (
                <div className="space-y-3">
                  {schedules.map((schedule) => (
                    <div key={schedule.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-bold text-gray-900">{schedule.name}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              schedule.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {schedule.isActive ? 'Active' : 'Paused'}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${FREQ_COLORS[schedule.frequency]?.bg || 'bg-gray-100'} ${FREQ_COLORS[schedule.frequency]?.text || 'text-gray-600'}`}>
                              {schedule.frequency}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {schedule.recipients?.length || 0} recipients
                            </span>
                            <span className="flex items-center gap-1">
                              <Download className="w-3 h-3" />
                              {schedule.exportFormat?.toUpperCase()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Play className="w-3 h-3" />
                              {schedule.runCount || 0} runs
                            </span>
                            {schedule.nextRunAt && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Next: {new Date(schedule.nextRunAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRunSchedule(schedule.id)}
                            disabled={generating === schedule.id}
                            className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                            title="Run now"
                          >
                            {generating === schedule.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleToggleSchedule(schedule.id, schedule.isActive)}
                            className="p-2 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg"
                            title={schedule.isActive ? 'Pause' : 'Resume'}
                          >
                            {schedule.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleDeleteSchedule(schedule.id)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── TAB: DASHBOARD BUILDER ──────────────── */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Dashboard Builder</h3>
                  <p className="text-sm text-gray-500">Configure dashboard tiles for your role. Drag to reorder.</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const roleDefault = ReportingEngine.getRoleDefaults()[currentUser?.role] || ReportingEngine.getRoleDefaults().admin;
                      setDashboardTiles(roleDefault.tiles);
                      actions.showToast?.('Reset to role defaults', 'info');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reset to Default
                  </button>
                  <button
                    onClick={handleSaveDashboard}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-indigo-700"
                  >
                    <Save className="w-4 h-4" />
                    Save Layout
                  </button>
                </div>
              </div>

              {/* Role Defaults */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
                <h4 className="font-semibold text-purple-800 mb-3">Default Dashboards by Role</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(ReportingEngine.getRoleDefaults()).map(([role, config]) => (
                    <button
                      key={role}
                      onClick={() => {
                        setDashboardTiles(config.tiles);
                        actions.showToast?.(`Loaded ${config.label} dashboard`, 'info');
                      }}
                      className="bg-white border border-purple-200 rounded-lg p-4 text-left hover:shadow-md transition-shadow"
                    >
                      <h5 className="font-bold text-purple-900">{config.label}</h5>
                      <p className="text-xs text-purple-600 mt-1">{config.tiles.length} tiles</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {config.tiles.slice(0, 4).map(t => (
                          <span key={t.id} className="px-2 py-0.5 bg-purple-100 text-purple-600 text-[10px] rounded-full">{t.title}</span>
                        ))}
                        {config.tiles.length > 4 && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-[10px] rounded-full">+{config.tiles.length - 4} more</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Tiles */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Current Dashboard Tiles ({dashboardTiles.length})</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {dashboardTiles.map((tile, idx) => (
                    <div
                      key={tile.id}
                      className={`bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-indigo-300 transition-all ${
                        tile.size === 'lg' ? 'md:col-span-2' : tile.size === 'md' ? '' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
                          <span className="font-medium text-sm">{tile.title}</span>
                        </div>
                        <button
                          onClick={() => setDashboardTiles(prev => prev.filter((_, i) => i !== idx))}
                          className="text-gray-300 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="px-1.5 py-0.5 bg-gray-100 rounded">{tile.type}</span>
                        <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded">{tile.size}</span>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setDashboardTiles(prev => [...prev, {
                        id: `tile-${Date.now()}`,
                        type: 'metric',
                        title: 'New Tile',
                        source: 'daily-occupancy',
                        size: 'sm'
                      }]);
                    }}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-4 flex items-center justify-center gap-2 text-gray-400 hover:text-indigo-500 hover:border-indigo-300 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="text-sm font-medium">Add Tile</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ─── TAB: GOVERNMENT REPORTS ─────────────── */}
          {activeTab === 'government' && (
            <div className="space-y-6">
              {/* Official Header */}
              <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-2xl p-8 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <Globe className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Australian Government — Regulatory Reports</h3>
                      <p className="text-slate-400 text-sm mt-0.5">Auto-compiled submissions for DoHAC &amp; ACQSC</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                    {[
                      { label: 'Total Reports', value: governmentReports.length, sub: 'regulatory templates' },
                      { label: 'Auto-Compile', value: governmentReports.filter(r => r.autoCompile).length, sub: 'fully automated' },
                      { label: 'Monthly Due', value: governmentReports.filter(r => r.frequency === 'monthly').length, sub: 'this month' },
                      { label: 'Quarterly Due', value: governmentReports.filter(r => r.frequency === 'quarterly').length, sub: 'this quarter' }
                    ].map(s => (
                      <div key={s.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                        <div className="text-2xl font-extrabold">{s.value}</div>
                        <div className="text-sm font-medium text-slate-300">{s.label}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{s.sub}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Government Report Cards */}
              <div className="space-y-4">
                {governmentReports.map((report) => {
                  const isExpanded = expandedGovReport === report.id;
                  const isGenerating = generating === report.id;
                  const colors = FREQ_COLORS[report.frequency] || FREQ_COLORS.ad_hoc;
                  const bodyStyle = GOV_BODY_STYLES[report.regulatoryBody] || { icon: Shield, color: 'gray', abbr: 'GOV' };
                  const BodyIcon = bodyStyle.icon;
                  const preview = govPreviewData[report.id];
                  const reportType = REPORT_TYPE_MAP[report.id];
                  return (
                    <div key={report.id} className={`bg-white border-2 ${isExpanded ? 'border-indigo-300 shadow-xl' : 'border-slate-200 hover:border-slate-300'} rounded-2xl overflow-hidden transition-all duration-300`}>
                      <div
                        className="p-6 cursor-pointer"
                        onClick={() => handleExpandGov(report.id)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4 flex-1 min-w-0">
                            <div className={`shrink-0 w-12 h-12 bg-gradient-to-br ${colors.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                              <Globe className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                <h4 className="font-bold text-slate-900 text-lg">{report.name}</h4>
                                <span className={`shrink-0 px-2.5 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${colors.bg} ${colors.text}`}>
                                  {report.frequency}
                                </span>
                                {report.autoCompile && (
                                  <span className="shrink-0 px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                                    <Zap className="w-3 h-3" /> Auto
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-600 leading-relaxed">{report.description}</p>
                              <div className="flex items-center gap-4 mt-3 flex-wrap">
                                <Tooltip content={GOV_REPORT_TOOLTIPS[report.regulatoryBody] || report.regulatoryBody} position="top">
                                  <span className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 px-2.5 py-1 rounded-lg cursor-help">
                                    <BodyIcon className="w-3.5 h-3.5" />
                                    <span className="font-medium">{bodyStyle.abbr}</span>
                                  </span>
                                </Tooltip>
                                <Tooltip content={`Submission deadline: ${report.dueDate || report.duePattern}`} position="top">
                                  <span className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 px-2.5 py-1 rounded-lg cursor-help">
                                    <Calendar className="w-3.5 h-3.5" />
                                    Due: <span className="font-medium text-slate-700">{report.dueDate || report.duePattern}</span>
                                  </span>
                                </Tooltip>
                                <Tooltip content={`Required submission format: ${report.format}`} position="top">
                                  <span className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 px-2.5 py-1 rounded-lg cursor-help">
                                    <FileText className="w-3.5 h-3.5" />
                                    <span className="font-medium">{report.format}</span>
                                  </span>
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGenerate(report.id);
                              }}
                              disabled={isGenerating}
                              className={`px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all shadow-sm ${
                                isGenerating
                                  ? 'bg-slate-100 text-slate-400'
                                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg hover:shadow-indigo-200'
                              }`}
                            >
                              {isGenerating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                              {isGenerating ? 'Compiling…' : 'Compile & Export'}
                            </button>
                            <div className={`p-2 rounded-lg transition-colors ${isExpanded ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400'}`}>
                              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                            </div>
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-slate-200 bg-gradient-to-b from-slate-50 to-white">
                          {/* Indicators / Sections */}
                          <div className="px-6 py-5 space-y-4">
                            {report.indicators && (
                              <div>
                                <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Quality Indicators Tracked</h5>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                  {report.indicators.map((ind, idx) => (
                                    <div key={ind} className="bg-white border border-blue-200 rounded-lg p-3 text-center hover:shadow-sm transition-shadow">
                                      <div className="text-blue-600 font-bold text-xs mb-0.5">QI {idx + 1}</div>
                                      <div className="text-[11px] text-slate-700 font-medium leading-tight">{ind}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {report.sections && (
                              <div>
                                <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Report Sections</h5>
                                <div className="flex flex-wrap gap-2">
                                  {report.sections.map((sec, idx) => (
                                    <span key={sec} className="px-3 py-1.5 bg-purple-50 text-purple-700 text-xs rounded-lg font-medium border border-purple-200 flex items-center gap-1.5">
                                      <span className="w-5 h-5 bg-purple-200 rounded text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>
                                      {sec}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Inline Data Preview */}
                            <div>
                              <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <Eye className="w-3.5 h-3.5" /> Live Data Preview
                              </h5>
                              {govPreviewLoading === report.id ? (
                                <div className="flex items-center justify-center py-12 gap-3">
                                  <RefreshCw className="w-5 h-5 animate-spin text-indigo-500" />
                                  <span className="text-sm text-slate-500 font-medium">Generating preview data…</span>
                                </div>
                              ) : preview && preview.data && preview.data.length > 0 ? (
                                <div className="space-y-4">
                                  {/* Summary stat row */}
                                  <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                                      <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Records</div>
                                      <div className="text-2xl font-bold text-slate-800 mt-0.5">{preview.data.length}</div>
                                    </div>
                                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                                      <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Data Fields</div>
                                      <div className="text-2xl font-bold text-slate-800 mt-0.5">{Object.keys(preview.data[0]).length}</div>
                                    </div>
                                    <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm">
                                      <div className="text-xs text-emerald-600 uppercase tracking-wide font-medium">Status</div>
                                      <div className="flex items-center gap-1.5 mt-1">
                                        <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                                        <span className="text-lg font-bold text-emerald-600">Ready</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Smart type-specific rendering */}
                                  {reportType === 'quality' ? (
                                    <QualityIndicatorGrid data={preview.data} />
                                  ) : reportType === 'financial' ? (
                                    <FinancialStatement data={preview.data} dateRange={preview.dateRange} />
                                  ) : reportType === 'compliance' ? (
                                    <ComplianceDashboard data={preview.data} />
                                  ) : (
                                    <EnhancedTable data={preview.data} />
                                  )}
                                </div>
                              ) : (
                                <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center">
                                  <Eye className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                                  <p className="text-sm text-slate-500">Click to load preview data</p>
                                  <button
                                    onClick={() => handleGovPreview(report.id)}
                                    className="mt-3 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg text-xs font-bold hover:bg-indigo-200 transition-colors"
                                  >
                                    Load Preview
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-2">
                              <button
                                onClick={() => {
                                  if (preview) setViewingReport(preview);
                                  else handleGenerate(report.id);
                                }}
                                className="px-4 py-2 bg-slate-800 text-white rounded-xl text-sm font-bold hover:bg-slate-700 flex items-center gap-2 transition-colors shadow-sm"
                              >
                                <Eye className="w-4 h-4" /> View Full Report
                              </button>
                              <button
                                onClick={() => {
                                  setScheduleConfig(prev => ({ ...prev, templateId: report.id, name: report.name, frequency: report.frequency }));
                                  setShowScheduleModal(true);
                                }}
                                className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl text-sm font-bold hover:bg-purple-200 flex items-center gap-2 transition-colors"
                              >
                                <Clock className="w-4 h-4" /> Schedule Auto-Submission
                              </button>
                              {preview && (
                                <button
                                  onClick={() => handleExport(preview.id, 'csv')}
                                  className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl text-sm font-bold hover:bg-emerald-200 flex items-center gap-2 transition-colors"
                                >
                                  <Download className="w-4 h-4" /> Export CSV
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── TAB: REPORT HISTORY ─────────────────── */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-800">Generated Report History</h3>
              {generatedReports.length === 0 ? (
                <EmptyState
                  icon={Activity}
                  title="No reports generated yet"
                  description="Generate a report from the Pre-built or Custom tabs to see it here."
                  action={
                    <button
                      onClick={() => setActiveTab('prebuilt')}
                      className="mt-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-semibold hover:bg-indigo-200"
                    >
                      Browse Pre-built Reports
                    </button>
                  }
                />
              ) : (
                generatedReports.map((report) => (
                  <div key={report.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-bold text-gray-900">{report.templateName || report.name}</h4>
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                            {report.status}
                          </span>
                          {report.isCustom && (
                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">Custom</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(report.generatedAt).toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Table className="w-3 h-3" />
                            {report.rowCount} rows
                          </span>
                          <span className="flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            {report.exportFormat?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setViewingReport(report)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleExport(report.id, 'csv')}
                          className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg"
                          title="Export CSV"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleExport(report.id, 'pdf')}
                          className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                          title="Export PDF"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ─── TAB: AI INSIGHTS ────────────────────── */}
          {activeTab === 'ai' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-purple-50 to-indigo-100 p-8 rounded-2xl border border-purple-200">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-purple-800 mb-2 flex items-center gap-3">
                      <Brain className="w-6 h-6 text-purple-600" />
                      AI-Powered Report Intelligence
                    </h3>
                    <p className="text-purple-600">Automated insights, anomaly detection, and optimisation recommendations</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm text-green-600 font-medium">AI Active</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Generation Speed', value: '87.3%', target: '85%', color: 'green', icon: Zap },
                    { label: 'Accuracy', value: '94.2%', target: '90%', color: 'blue', icon: Target },
                    { label: 'Automation', value: '78.5%', target: '75%', color: 'purple', icon: Cpu },
                    { label: 'Insights Quality', value: '91.8%', target: '90%', color: 'green', icon: Sparkles }
                  ].map((metric) => {
                    const Icon = metric.icon;
                    return (
                      <div key={metric.label} className="bg-white p-5 rounded-xl shadow-sm border border-purple-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className={`p-2 bg-${metric.color}-100 rounded-lg`}>
                            <Icon className={`w-5 h-5 text-${metric.color}-600`} />
                          </div>
                          <div className="text-right">
                            <div className={`text-2xl font-bold text-${metric.color}-600`}>{metric.value}</div>
                            <div className="text-xs text-gray-500">{metric.label}</div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div className={`bg-${metric.color}-500 h-1.5 rounded-full`} style={{ width: metric.value }} />
                        </div>
                        <div className="text-xs text-gray-400 mt-1">Target: {metric.target}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">AI Recommendations</h4>
                  <div className="space-y-3">
                    {[
                      { title: 'Automate monthly quality reports', impact: '2.5 hr/month saved', confidence: 92, priority: 'High' },
                      { title: 'Add predictive analytics to financials', impact: 'Better forecasting', confidence: 88, priority: 'Medium' },
                      { title: 'Optimise report templates', impact: '15% readability improvement', confidence: 76, priority: 'Low' }
                    ].map((rec, i) => (
                      <div key={i} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="font-medium text-sm">{rec.title}</h5>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            rec.priority === 'High' ? 'bg-red-100 text-red-700' :
                            rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                          }`}>{rec.priority}</span>
                        </div>
                        <p className="text-xs text-gray-500">{rec.impact} &bull; {rec.confidence}% confidence</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Predictive Insights</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'Trend Analysis', value: 6, desc: 'Patterns found', color: 'green' },
                      { label: 'Anomaly Detection', value: 3, desc: 'Unusual data', color: 'red' },
                      { label: 'Optimisation', value: 8, desc: 'Opportunities', color: 'blue' },
                      { label: 'Risk Alerts', value: 2, desc: 'Need attention', color: 'yellow' }
                    ].map((ins) => (
                      <div key={ins.label} className={`text-center p-4 bg-${ins.color}-50 rounded-lg`}>
                        <div className={`text-2xl font-bold text-${ins.color}-600`}>{ins.value}</div>
                        <div className="text-sm text-gray-700 font-medium">{ins.label}</div>
                        <div className="text-xs text-gray-500">{ins.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── REPORT VIEWER MODAL ──────────────────────── */}
      {viewingReport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setViewingReport(null)}>
          <div ref={viewerRef} className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Professional Report Header */}
            <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-900 text-white relative overflow-hidden shrink-0">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />
              <div className="relative p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/10">
                      <FileText className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-medium">MedFast Aged Care</span>
                        <span className="w-1 h-1 bg-slate-500 rounded-full" />
                        <span className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-medium">Official Report</span>
                      </div>
                      <h3 className="text-xl font-bold tracking-tight">{viewingReport.templateName || viewingReport.name}</h3>
                      <div className="flex items-center gap-4 mt-2.5 flex-wrap">
                        <span className="flex items-center gap-1.5 text-sm text-slate-300">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(viewingReport.generatedAt).toLocaleDateString('en-AU', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1.5 text-sm text-slate-300">
                          <Table className="w-3.5 h-3.5" />
                          {viewingReport.rowCount} records
                        </span>
                        {viewingReport.frequency && (
                          <span className="px-2.5 py-0.5 bg-white/10 rounded-lg text-xs font-medium capitalize backdrop-blur-sm border border-white/10">
                            {viewingReport.frequency}
                          </span>
                        )}
                        {viewingReport.category && (
                          <span className="px-2.5 py-0.5 bg-indigo-500/20 rounded-lg text-xs font-medium capitalize text-indigo-200 border border-indigo-400/20">
                            {viewingReport.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleExport(viewingReport.id, 'csv')}
                      className="px-3.5 py-2 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-colors border border-emerald-500/20"
                    >
                      <Download className="w-3.5 h-3.5" />CSV
                    </button>
                    <button
                      onClick={() => handleExport(viewingReport.id, 'pdf')}
                      className="px-3.5 py-2 bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-colors border border-purple-500/20"
                    >
                      <Printer className="w-3.5 h-3.5" />PDF
                    </button>
                    <button
                      onClick={() => handleExport(viewingReport.id, 'json')}
                      className="px-3.5 py-2 bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-colors border border-blue-500/20"
                    >
                      <Download className="w-3.5 h-3.5" />JSON
                    </button>
                    <Tooltip content="Ctrl+P to print • Esc to close" position="bottom">
                      <button onClick={() => setViewingReport(null)} className="p-2.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-xl transition-colors ml-1">
                        <X className="w-5 h-5" />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>

            {/* Report Body */}
            <div className="flex-1 overflow-auto bg-gradient-to-b from-slate-50 to-white">
              {viewingReport.data && viewingReport.data.length > 0 ? (
                <div className="p-6 space-y-6">
                  {/* Summary Stats Bar */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                      <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Total Records</div>
                      <div className="text-2xl font-extrabold text-slate-800 mt-1 tabular-nums">{viewingReport.data.length}</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                      <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Data Columns</div>
                      <div className="text-2xl font-extrabold text-slate-800 mt-1 tabular-nums">{Object.keys(viewingReport.data[0]).length}</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                      <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Report Period</div>
                      <div className="text-2xl font-extrabold text-slate-800 mt-1 capitalize">{viewingReport.frequency || 'Ad Hoc'}</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-emerald-200 shadow-sm">
                      <div className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider">Status</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                        <span className="text-lg font-extrabold text-emerald-600">Complete</span>
                      </div>
                    </div>
                  </div>

                  {/* Smart Type-Specific Rendering */}
                  {(() => {
                    const templateId = viewingReport.templateId || '';
                    const reportType = REPORT_TYPE_MAP[templateId];
                    if (reportType === 'quality') return <QualityIndicatorGrid data={viewingReport.data} />;
                    if (reportType === 'financial') return <FinancialStatement data={viewingReport.data} dateRange={viewingReport.dateRange} />;
                    if (reportType === 'compliance') return <ComplianceDashboard data={viewingReport.data} />;
                    return <EnhancedTable data={viewingReport.data} />;
                  })()}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                    <FileText className="w-10 h-10 text-slate-300" />
                  </div>
                  <p className="text-slate-500 text-lg font-medium">No data available for this report</p>
                  <p className="text-slate-400 text-sm mt-1">Try regenerating with different parameters</p>
                </div>
              )}
            </div>

            {/* Report Footer */}
            <div className="border-t border-slate-200 bg-white px-6 py-3.5 flex items-center justify-between text-xs text-slate-400 shrink-0">
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5" />
                <span className="font-medium">MedFast Aged Care — Confidential Report</span>
              </div>
              <span className="tabular-nums">Generated {new Date(viewingReport.generatedAt).toLocaleString('en-AU')}</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── SCHEDULE MODAL ───────────────────────────── */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowScheduleModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">Create Report Schedule</h3>
              <button onClick={() => setShowScheduleModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report Template</label>
                <select
                  value={scheduleConfig.templateId}
                  onChange={(e) => {
                    const tpl = [...prebuiltReports, ...governmentReports].find(r => r.id === e.target.value);
                    setScheduleConfig(prev => ({ ...prev, templateId: e.target.value, name: tpl?.name || prev.name }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select a report…</option>
                  <optgroup label="Pre-built Reports">
                    {prebuiltReports.map(r => <option key={r.id} value={r.id}>{r.name} ({r.frequency})</option>)}
                  </optgroup>
                  <optgroup label="Government Reports">
                    {governmentReports.map(r => <option key={r.id} value={r.id}>{r.name} ({r.frequency})</option>)}
                  </optgroup>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Name</label>
                <input
                  type="text"
                  value={scheduleConfig.name}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <select
                  value={scheduleConfig.frequency}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, frequency: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipients (comma-separated emails)</label>
                <input
                  type="text"
                  value={scheduleConfig.recipients}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, recipients: e.target.value }))}
                  placeholder="admin@facility.com, manager@facility.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Export Format</label>
                <select
                  value={scheduleConfig.exportFormat}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, exportFormat: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  {EXPORT_FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                </select>
              </div>
              <button
                onClick={handleCreateSchedule}
                disabled={!scheduleConfig.templateId}
                className="w-full py-2.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Clock className="w-4 h-4" />
                Create Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
